import * as React from 'react'
import { ChevronRight, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

/*
 * The handful of form controls the irrigation sheets use. Sized for a thumb
 * on a phone (44px) and back to the app's 36px at a desk, the rule
 * field-survey.md gives for the tree sheet.
 */

const CONTROL_CLASS =
  'h-11 w-full rounded-md border border-default bg-surface px-2.5 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 lg:h-9'

interface LabeledProps {
  label: string
  id: string
  hint?: string | undefined
  children: React.ReactNode
}

/** A label stacked above its control, with an optional hint below. */
export function Labeled({ label, id, hint, children }: LabeledProps) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={id}
        className="block text-xs font-medium text-secondary-text"
      >
        {label}
      </label>
      {children}
      {hint === undefined ? null : (
        <p className="text-xs text-muted-text">{hint}</p>
      )}
    </div>
  )
}

interface TextFieldProps {
  label: string
  id: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  hint?: string
  multiline?: boolean
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}

/** Text where empty means "not recorded" (null), never an empty string. */
export function TextField({
  label,
  id,
  value,
  onChange,
  placeholder,
  hint,
  multiline,
  inputMode,
}: TextFieldProps) {
  const handle = (next: string): void => {
    onChange(next.trim() === '' ? null : next)
  }
  return (
    <Labeled label={label} id={id} hint={hint}>
      {multiline === true ? (
        <textarea
          id={id}
          className={cn(CONTROL_CLASS, 'h-20 py-2 lg:h-20')}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(event) => {
            handle(event.target.value)
          }}
        />
      ) : (
        <input
          id={id}
          className={CONTROL_CLASS}
          value={value ?? ''}
          placeholder={placeholder}
          inputMode={inputMode}
          onChange={(event) => {
            handle(event.target.value)
          }}
        />
      )}
    </Labeled>
  )
}

type SelectFieldProps<T extends string> = {
  label: string
  id: string
  /** Null while nothing is recorded yet, including when a choice is required. */
  value: T | null
  options: readonly { value: T; label: string }[]
  hint?: string
} & (
  | {
      /** What "nothing chosen" is called. The control can clear the choice. */
      emptyLabel: string
      onChange: (value: T | null) => void
    }
  | {
      /**
       * Omit to make a choice required. A value that is not one of the
       * options is ignored, so the control cannot write null back.
       */
      emptyLabel?: undefined
      onChange: (value: T) => void
    }
)

/**
 * A `<select>` over a closed list. The value comes back by matching the
 * option list rather than by casting the string, so an option the API would
 * refuse cannot leave this component.
 */
export function SelectField<T extends string>(props: SelectFieldProps<T>) {
  const { label, id, value, options, hint } = props
  return (
    <Labeled label={label} id={id} hint={hint}>
      <select
        id={id}
        className={CONTROL_CLASS}
        value={value ?? ''}
        onChange={(event) => {
          const chosen = options.find(
            (option) => option.value === event.target.value,
          )
          if (props.emptyLabel === undefined) {
            if (chosen !== undefined) props.onChange(chosen.value)
            return
          }
          props.onChange(chosen?.value ?? null)
        }}
      >
        {props.emptyLabel === undefined ? null : (
          <option value="">{props.emptyLabel}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Labeled>
  )
}

interface StepperProps {
  label: string
  value: number
  min?: number
  max?: number
  onChange: (value: number) => void
}

/** A count with − and + buttons, for head counts and how many a problem covers. */
export function Stepper({
  label,
  value,
  min = 0,
  max = 999,
  onChange,
}: StepperProps) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">{label}</span>
      <span className="flex items-center gap-1">
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-md border border-default text-secondary-text hover:bg-subtle disabled:opacity-40 lg:h-8 lg:w-8"
          aria-label={`Fewer ${label.toLowerCase()}`}
          disabled={value <= min}
          onClick={() => {
            onChange(Math.max(min, value - 1))
          }}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span
          className="w-10 text-center text-sm tabular-nums"
          aria-live="polite"
        >
          {value}
        </span>
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-md border border-default text-secondary-text hover:bg-subtle disabled:opacity-40 lg:h-8 lg:w-8"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={value >= max}
          onClick={() => {
            onChange(Math.min(max, value + 1))
          }}
        >
          <Plus className="h-4 w-4" />
        </button>
      </span>
    </div>
  )
}

type ChipsProps<T extends string | number> = {
  label: string
  value: T | null
  options: readonly { value: T; label: string }[]
} & (
  | {
      /** Offer a chip that clears the choice, and call it this. */
      noneLabel: string
      onChange: (value: T | null) => void
    }
  | {
      /** Omit when a choice is required. The chips cannot clear it. */
      noneLabel?: undefined
      onChange: (value: T) => void
    }
)

/** A short closed choice as a row of chips: severity, a meter or a well. */
export function Chips<T extends string | number>(props: ChipsProps<T>) {
  const { label, value, options } = props
  const chip = (selected: boolean) =>
    cn(
      'h-11 min-w-11 rounded-full border px-3 text-sm transition-colors lg:h-8',
      selected
        ? 'border-brand bg-brand text-primary-foreground'
        : 'border-default bg-surface text-secondary-text hover:bg-subtle',
    )
  return (
    <fieldset className="space-y-1">
      <legend className="text-xs font-medium text-secondary-text">
        {label}
      </legend>
      <div className="flex flex-wrap gap-1.5">
        {props.noneLabel === undefined ? null : (
          <button
            type="button"
            className={chip(value === null)}
            aria-pressed={value === null}
            onClick={() => {
              props.onChange(null)
            }}
          >
            {props.noneLabel}
          </button>
        )}
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            className={chip(value === option.value)}
            aria-pressed={value === option.value}
            onClick={() => {
              props.onChange(option.value)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

/** A row that opens another record's sheet. */
export function LinkRow({
  title,
  detail,
  pending,
  tone,
  onClick,
}: {
  title: string
  detail?: string | undefined
  pending?: boolean
  tone?: 'problem'
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-subtle lg:py-2"
        onClick={onClick}
      >
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-sm',
              tone === 'problem' ? 'text-danger' : null,
            )}
          >
            {title}
          </span>
          {detail === undefined ? null : (
            <span className="block truncate text-xs text-muted-text">
              {detail}
            </span>
          )}
        </span>
        {pending === true ? (
          <span className="text-xs text-muted-text">Not synced</span>
        ) : null}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
      </button>
    </li>
  )
}

export function Section({
  title,
  children,
  action,
}: {
  title: string
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-text">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  )
}
